import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkles, Eye, Plus, X, Rocket } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Spark } from "@/shared/components/brand/Spark";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { StatusText } from "@/shared/components/ui/status-chip";
import { InfoHint } from "@/shared/components/ui/info-hint";
import { KpiStrip, KpiTile } from "@/shared/components/ui/kpi";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useCampaignDetail } from "../use-campaign";
import {
  useCampaignSequences,
  useCreateSequence,
  useGenerateCopy,
  useLaunchSequence,
  usePreview,
} from "../use-sequences";
import { LeadsBrowser } from "../leads/LeadsBrowser";
import { useTenantUsers } from "@/features/tasks/use-users";
import { useAuth } from "@/shared/auth/useAuth";
import type { SequenceSummary, Template } from "../types";

interface StepDraft {
  /** ADR-0027: EMAIL sends a template; MANUAL_TASK is a human to-do assigned to a user. */
  type: "EMAIL" | "MANUAL_TASK";
  templateId: string;
  delayDays: number;
  /** ADR-0025: optional per-step brief (tone/angle) for generating this step's copy. */
  brief: string;
  // MANUAL_TASK fields (ADR-0027).
  assigneeUserId: string;
  taskTitle: string;
  taskInstructions: string;
  taskDueOffsetDays: number;
  isBlocker: boolean;
}

const emptyStep = (templateId: string, delayDays: number): StepDraft => ({
  type: "EMAIL",
  templateId,
  delayDays,
  brief: "",
  assigneeUserId: "",
  taskTitle: "",
  taskInstructions: "",
  taskDueOffsetDays: 2,
  isBlocker: true,
});

/**
 * Sequences (screens.md §8-9) — the outreach build: author copy, preview it over
 * the frozen audience, assemble the drip steps, and launch (enroll the audience
 * with the provider). Copy → Sequence → Launch in one screen.
 */
export function SequencesTab() {
  const { id: campaignId } = useParams<{ id: string }>();
  const cid = campaignId ?? "";
  const detail = useCampaignDetail(cid);
  const templates = detail.data?.templates ?? [];

  const gen = useGenerateCopy(cid);
  const preview = usePreview(cid);
  const createSeq = useCreateSequence(cid);
  const launch = useLaunchSequence(cid);
  const campaignSequences = useCampaignSequences(cid);

  const [prompt, setPrompt] = useState("");
  const [name, setName] = useState("Intro drip");
  const [steps, setSteps] = useState<StepDraft[]>([]);
  const [genStep, setGenStep] = useState<number | null>(null);

  // ADR-0027: tenant users for the manual-task assignee picker; the current user is
  // labelled "Me" and floated to the top.
  const usersQ = useTenantUsers();
  const me = useAuth().user;
  const assigneeOptions = (usersQ.data?.items ?? [])
    .map((u) => ({ userId: u.userId, label: u.userId === me?.userId ? `Me (${u.email})` : u.email }))
    .sort((a, b) => (a.userId === me?.userId ? -1 : b.userId === me?.userId ? 1 : 0));

  // Seed the first step from the latest template once copy exists.
  useEffect(() => {
    if (steps.length === 0 && templates.length > 0) {
      setSteps([emptyStep(templates[0].id, 0)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates.length]);

  // ADR-0025: generate a template tailored to ONE step (its brief + the tenant's
  // per-step follow-up guidance) and assign the result to that step.
  async function generateForStep(i: number) {
    setGenStep(i);
    try {
      const tpl = await gen.mutateAsync({ prompt: steps[i]?.brief?.trim() || undefined, stepIndex: i });
      updateStep(setSteps, i, { templateId: tpl.id });
    } finally {
      setGenStep(null);
    }
  }

  const created = createSeq.data;
  const launched = launch.data;
  // The brief is OPTIONAL — copy is always authored from the AI & Branding profile, so an
  // empty box still generates (using the profile + the campaign's vertical alone). All copy
  // generation (top + per-step) shares one mutation, so guard them together.
  const copyBusy = gen.isPending || genStep !== null;
  const canGenerate = !copyBusy;
  const canCreate =
    !!name.trim() &&
    steps.length > 0 &&
    steps.every((s) => (s.type === "MANUAL_TASK" ? !!s.assigneeUserId && !!s.taskTitle.trim() : !!s.templateId)) &&
    !createSeq.isPending;

  const existing = campaignSequences.data?.items ?? [];

  return (
    <div className="space-y-8">
      {/* Existing sequences for THIS campaign (ADR-0025) — so a created sequence stays
          visible here instead of only in the global Sequences page. */}
      {existing.length > 0 && (
        <section className="space-y-3" data-testid="campaign-sequences">
          <p className="ge-eyebrow flex items-center gap-1">
            SEQUENCES IN THIS CAMPAIGN
            <InfoHint text="Every drip you've built on this campaign, with its status and how many contacts are enrolled. Launch a draft, or build another below." />
          </p>
          <div className="space-y-2">
            {existing.map((s) => (
              <SequenceRow key={s.id} s={s} onLaunch={() => launch.mutate(s.id)} launching={launch.isPending} />
            ))}
          </div>
        </section>
      )}

      {/* 1 — Copy */}
      <section className="space-y-3">
        <p className="ge-eyebrow flex items-center gap-1">
          1 · COPY
          <InfoHint
            side="bottom"
            text="Copy is always written in your workspace voice from AI & Branding. This box is optional — add a tone or angle just for this campaign, or leave it blank."
          />
        </p>
        <Card className="p-[18px]">
          <p className="mb-2 text-[12.5px] text-ge-secondary">
            Optional — we already use your AI &amp; Branding profile. Add a tone or angle for this campaign, or
            just generate.
          </p>
          <div className="flex gap-3">
            <Spark size={16} className="mt-1 shrink-0" />
            <textarea
              data-testid="copy-prompt"
              rows={2}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Lead with a local angle and one clear CTA (optional)"
              className="w-full resize-none bg-transparent text-[15px] leading-[1.5] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none"
            />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="ml-auto">
              <Button data-testid="generate-copy" disabled={!canGenerate} onClick={() => gen.mutate({ prompt: prompt.trim() || undefined })}>
                <Sparkles className="h-[14px] w-[14px]" strokeWidth={2.2} />
                {gen.isPending ? "Writing…" : "Generate copy"}
              </Button>
            </div>
          </div>
          {gen.isError && (
            <p className="mt-2 text-[12px] text-ge-danger" data-testid="copy-error">
              Copy generation failed. Try again.
            </p>
          )}
        </Card>

        {templates.length > 0 && (
          <div className="space-y-2" data-testid="templates">
            {templates.map((t) => (
              <TemplateCard key={t.id} t={t} />
            ))}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="preview-btn"
                disabled={preview.isPending}
                onClick={() => preview.mutate()}
              >
                <Eye className="h-[14px] w-[14px]" strokeWidth={2} />
                {preview.isPending ? "Rendering…" : "Preview over audience"}
              </Button>
              {preview.isError && (
                <span className="text-[12px] text-ge-danger" data-testid="preview-error">
                  Freeze the audience and generate copy first.
                </span>
              )}
            </div>
          </div>
        )}

        {preview.data && (
          <Card className="space-y-3 p-[18px]" data-testid="preview-result">
            <div className="flex items-center gap-3">
              <p className="ge-eyebrow">PREVIEW · {preview.data.count} EMAILS</p>
              {preview.data.missingFields.length > 0 && (
                <StatusText tone="warning">
                  unknown tokens: {preview.data.missingFields.join(", ")}
                </StatusText>
              )}
              {preview.data.subjectFormatOk === false && (
                <StatusText tone="warning" data-testid="subject-format-warning">
                  subject may not match your structure (missing {(preview.data.subjectFormatMissingTokens ?? []).map((t) => `{{${t}}}`).join(", ")}) — regenerate to apply
                </StatusText>
              )}
            </div>
            {preview.data.emails.slice(0, 3).map((e) => (
              <div key={e.leadId} className="rounded-ge-control border border-ge-border-card p-3">
                <p className="text-[13px] font-semibold text-ge-primary">{e.subject}</p>
                <p className="mt-1 whitespace-pre-wrap text-[12.5px] text-ge-secondary">{e.body}</p>
              </div>
            ))}
          </Card>
        )}
      </section>

      {/* 2 — Sequence */}
      <section className="space-y-3">
        <p className="ge-eyebrow flex items-center gap-1">2 · BUILD SEQUENCE <InfoHint text="The follow-up drip: an ordered list of steps, each a template + a delay in days. Step 1 goes out first, the rest follow on their delays." /></p>
        {templates.length === 0 ? (
          <EmptyState message="Generate copy above first — a sequence step needs a template to send." />
        ) : (
          <Card className="space-y-4 p-[18px]">
            <label className="block">
              <span className="font-mono text-[11px] text-ge-secondary">SEQUENCE NAME</span>
              <Input
                data-testid="seq-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-[280px]"
              />
            </label>

            <div className="space-y-2">
              {steps.map((s, i) => (
                <div key={i} className="rounded-ge-param border border-ge-border-card p-3" data-testid={`step-${i}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-[11px] text-ge-faint">#{i + 1}</span>
                    {/* ADR-0027: EMAIL vs MANUAL_TASK */}
                    <div className="flex overflow-hidden rounded-ge-control border border-ge-border-card">
                      {(["EMAIL", "MANUAL_TASK"] as const).map((ty) => (
                        <button
                          key={ty}
                          type="button"
                          data-testid={`step-type-${ty === "EMAIL" ? "email" : "task"}-${i}`}
                          onClick={() => updateStep(setSteps, i, { type: ty })}
                          className={cn(
                            "px-2 py-1 text-[11px]",
                            s.type === ty ? "bg-ge-active font-semibold text-ge-primary" : "text-ge-secondary",
                          )}
                        >
                          {ty === "EMAIL" ? "Email" : "Manual task"}
                        </button>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 font-mono text-[11px] text-ge-secondary">
                      {s.type === "MANUAL_TASK" ? "due in" : "delay"}
                      <input
                        data-testid={`step-delay-${i}`}
                        type="number"
                        min={0}
                        value={s.type === "MANUAL_TASK" ? s.taskDueOffsetDays : s.delayDays}
                        onChange={(e) =>
                          updateStep(
                            setSteps,
                            i,
                            s.type === "MANUAL_TASK"
                              ? { taskDueOffsetDays: Math.max(0, Number(e.target.value) || 0) }
                              : { delayDays: Math.max(0, Number(e.target.value) || 0) },
                          )
                        }
                        className="w-12 rounded-ge-control border border-ge-border-card bg-transparent px-1 py-1 text-ge-value focus-visible:outline-none"
                      />
                      d
                    </label>
                    {steps.length > 1 && (
                      <button
                        type="button"
                        data-testid={`remove-step-${i}`}
                        onClick={() => setSteps((prev) => prev.filter((_, j) => j !== i))}
                        className="ml-auto text-ge-muted hover:text-ge-danger"
                      >
                        <X className="h-[14px] w-[14px]" />
                      </button>
                    )}
                  </div>

                  {s.type === "EMAIL" ? (
                    <>
                      <div className="mt-2">
                        <select
                          data-testid={`step-template-${i}`}
                          value={s.templateId}
                          onChange={(e) => updateStep(setSteps, i, { templateId: e.target.value })}
                          className="rounded-ge-control border border-ge-border-card bg-transparent px-2 py-1 text-[12.5px] text-ge-value"
                        >
                          {templates.map((t) => (
                            <option key={t.id} value={t.id} className="bg-ge-card">
                              {t.subject.slice(0, 40)}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* ADR-0025: per-step tone/brief + generate a template just for this step. */}
                      <div className="mt-2 flex items-center gap-2">
                        <Input
                          data-testid={`step-brief-${i}`}
                          value={s.brief}
                          onChange={(e) => updateStep(setSteps, i, { brief: e.target.value })}
                          placeholder={i === 0 ? "Tone/angle for this email (optional)" : "e.g. shorter, reference the first email (optional)"}
                          className="h-8 flex-1 text-[12px]"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`step-generate-${i}`}
                          disabled={copyBusy}
                          onClick={() => void generateForStep(i)}
                        >
                          <Sparkles className="h-[13px] w-[13px]" strokeWidth={2.2} />
                          {genStep === i ? "Writing…" : "Generate"}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="mt-2 space-y-2" data-testid={`step-task-${i}`}>
                      <AssigneeCombobox
                        testId={`step-assignee-${i}`}
                        value={s.assigneeUserId}
                        options={assigneeOptions}
                        onChange={(id) => updateStep(setSteps, i, { assigneeUserId: id })}
                      />
                      <Input
                        data-testid={`step-task-title-${i}`}
                        value={s.taskTitle}
                        onChange={(e) => updateStep(setSteps, i, { taskTitle: e.target.value })}
                        placeholder="Task title — e.g. Call {{name}}"
                        className="h-8 w-full text-[12px]"
                      />
                      <textarea
                        data-testid={`step-task-instructions-${i}`}
                        rows={2}
                        value={s.taskInstructions}
                        onChange={(e) => updateStep(setSteps, i, { taskInstructions: e.target.value })}
                        placeholder="Instructions (optional) — supports {{name}}, {{city}}, {{contact_name}}, {{contact_phone}}…"
                        className="w-full resize-none rounded-ge-control border border-ge-border-card bg-transparent px-2 py-1 text-[12px] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none"
                      />
                      <label className="flex items-center gap-2 font-mono text-[11px] text-ge-secondary">
                        <input
                          type="checkbox"
                          data-testid={`step-blocker-${i}`}
                          checked={s.isBlocker}
                          onChange={(e) => updateStep(setSteps, i, { isBlocker: e.target.checked })}
                        />
                        Blocks the sequence until done
                        <InfoHint text="When on, the drip pauses at this step until the assignee completes the task, then continues." />
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="add-step"
                onClick={() => setSteps((prev) => [...prev, emptyStep(templates[0].id, 3)])}
              >
                <Plus className="h-[14px] w-[14px]" strokeWidth={2.2} /> Add step
              </Button>
              <div className="ml-auto">
                <Button
                  data-testid="create-sequence"
                  disabled={!canCreate}
                  onClick={() =>
                    createSeq.mutate({
                      name: name.trim(),
                      steps: steps.map((s, i) =>
                        s.type === "MANUAL_TASK"
                          ? {
                              order: i + 1,
                              delayDays: s.delayDays,
                              type: "MANUAL_TASK" as const,
                              assigneeUserId: s.assigneeUserId,
                              taskTitle: s.taskTitle.trim(),
                              taskInstructions: s.taskInstructions.trim(),
                              taskDueOffsetDays: s.taskDueOffsetDays,
                              isBlocker: s.isBlocker,
                            }
                          : { order: i + 1, delayDays: s.delayDays, templateId: s.templateId },
                      ),
                    })
                  }
                >
                  {createSeq.isPending ? "Creating…" : "Create sequence"}
                </Button>
              </div>
            </div>
            {createSeq.isError && (
              <p className="text-[12px] text-ge-danger" data-testid="create-error">
                Couldn&apos;t create the sequence. Check the steps and try again.
              </p>
            )}
          </Card>
        )}
      </section>

      {/* 3 — Launch */}
      {created && (
        <section className="space-y-3" data-testid="sequence-created">
          <p className="ge-eyebrow flex items-center gap-1">3 · LAUNCH <InfoHint text="Hands the sequence to the sending provider (Smartlead), which enrolls your frozen audience and manages cadence, warmup and throttling." /></p>
          <Card className="flex flex-wrap items-center gap-3 p-[18px]">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-[14px] font-semibold text-ge-primary">{created.name}</p>
                <StatusText tone={created.status === "LAUNCHED" || launched ? "accent" : "faint"}>
                  {launched ? "LAUNCHED" : created.status}
                </StatusText>
              </div>
              <p className="text-[12.5px] text-ge-secondary">
                {created.steps.length} step{created.steps.length === 1 ? "" : "s"}. Launching enrolls the
                frozen, eligible audience (verified, not opted-out) with the provider.
              </p>
            </div>
            <Button
              data-testid="launch-btn"
              disabled={!!launched || launch.isPending}
              onClick={() => launch.mutate(created.id)}
            >
              <Rocket className="h-[14px] w-[14px]" strokeWidth={2.2} />
              {launch.isPending ? "Launching…" : launched ? "Launched" : "Launch sequence"}
            </Button>
          </Card>

          {launch.isError && (
            <p className="text-[12px] text-ge-danger" data-testid="launch-error">
              Launch failed — there may be no eligible leads yet. Enrich + freeze first.
            </p>
          )}

          {launched && (
            <div data-testid="launch-result">
              <KpiStrip>
                <KpiTile label="Enrolled" value={String(launched.enrolled)} highlighted />
                <KpiTile label="Provider ref" value={launched.providerRef ?? "—"} />
              </KpiStrip>
            </div>
          )}
        </section>
      )}

      {/* Affected leads (ADR-0025) — the approved audience this sequence targets, right here
          so you don't have to jump to the Leads screen. Opens the same detail drawer. */}
      <section className="space-y-3" data-testid="sequence-leads">
        <p className="ge-eyebrow flex items-center gap-1">
          AUDIENCE (APPROVED LEADS)
          <InfoHint text="The approved leads in this campaign — the audience a launch enrolls. Open any one to see its full profile and engagement." />
        </p>
        <LeadsBrowser campaignId={cid} initialStatus="APPROVED" />
      </section>
    </div>
  );
}

function SequenceRow({ s, onLaunch, launching }: { s: SequenceSummary; onLaunch: () => void; launching: boolean }) {
  const isLaunched = s.status === "LAUNCHED";
  return (
    <Card className="flex flex-wrap items-center gap-3 p-[14px]" data-testid={`campaign-seq-${s.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-[13.5px] font-semibold text-ge-primary">{s.name}</p>
          <StatusText tone={isLaunched ? "accent" : "faint"}>{s.status}</StatusText>
        </div>
        <p className="font-mono text-[11px] text-ge-muted">
          {s.steps} step{s.steps === 1 ? "" : "s"} · {s.enrolled} enrolled
        </p>
      </div>
      {!isLaunched && (
        <Button variant="outline" size="sm" data-testid={`campaign-seq-launch-${s.id}`} disabled={launching} onClick={onLaunch}>
          <Rocket className="h-[13px] w-[13px]" strokeWidth={2.2} />
          {launching ? "Launching…" : "Launch"}
        </Button>
      )}
    </Card>
  );
}

function updateStep(
  setSteps: React.Dispatch<React.SetStateAction<StepDraft[]>>,
  i: number,
  patch: Partial<StepDraft>,
) {
  setSteps((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
}

/** ADR-0027: searchable assignee picker. Lightweight (no combobox lib) — a text input
 * that filters the tenant users client-side and a dropdown of matches. */
function AssigneeCombobox({
  testId,
  value,
  options,
  onChange,
}: {
  testId: string;
  value: string;
  options: { userId: string; label: string }[];
  onChange: (userId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = options.find((o) => o.userId === value);
  const filtered = q.trim() ? options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase())) : options;

  return (
    <div className="relative w-[280px]">
      <input
        data-testid={testId}
        value={open ? q : selected?.label ?? ""}
        onFocus={() => {
          setOpen(true);
          setQ("");
        }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        placeholder="Search and assign…"
        className="h-8 w-full rounded-ge-control border border-ge-border-card bg-transparent px-2 text-[12.5px] text-ge-value placeholder:text-ge-muted focus-visible:outline-none"
      />
      {open && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-ge-control border border-ge-border-card bg-ge-card shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-2 py-1.5 text-[12px] text-ge-muted">No matches</div>
          ) : (
            filtered.map((o) => (
              <button
                key={o.userId}
                type="button"
                data-testid={`${testId}-opt-${o.userId}`}
                // keep the input focused so onBlur doesn't fire before the click
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(o.userId);
                  setOpen(false);
                }}
                className={cn(
                  "block w-full px-2 py-1.5 text-left text-[12.5px] hover:bg-ge-active",
                  o.userId === value ? "font-semibold text-ge-primary" : "text-ge-value",
                )}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TemplateCard({ t }: { t: Template }) {
  return (
    <Card className={cn("p-3")} data-testid={`template-${t.id}`}>
      <p className="text-[13px] font-semibold text-ge-primary">{t.subject}</p>
      <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-[12.5px] text-ge-secondary">{t.body}</p>
    </Card>
  );
}
